/**
 * Wording and judgements of the payments screens (P6-e2): the state of a gateway attempt, the kinds of the exception queue, the paid
 * column of the reservation list and the typed amount. The server owns every money rule (balances, what can be refunded, the
 * idempotency of a refund); this file only words what it answers and refuses an obviously wrong box before the call.
 */

import type { BadgeTone } from '@/components/Badge';
import type { Platba, PlatbaVyjimka, Rezervace, Uhrada, UhradaPrehled } from './api';

/** The biggest amount the API takes in one payment / refund (`RucniUhradaBodySchema`, `VraceniBodySchema`). */
export const CASTKA_MAX = 10_000_000;
/** Longest note the API stores with a payment / refund / settled item. */
export const POZNAMKA_MAX = 200;

export const PROVIDER_NAZEV: Record<string, string> = {
  test: 'Testovací brána',
  prevod: 'Bankovní převod',
  csob: 'ČSOB',
};

export const providerNazev = (provider: string): string => PROVIDER_NAZEV[provider] ?? provider;

export const METODA_NAZEV: Record<string, string> = {
  karta: 'Karta',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  prevod: 'Převod',
};

export const metodaNazev = (metoda: string): string => METODA_NAZEV[metoda] ?? metoda;

/** "Přes bránu · Karta", "Na pobočce" or "Původní systém" (a credit carried over from the legacy system). */
export function zpusobUhrady(u: Pick<UhradaPrehled, 'zdroj' | 'provider' | 'metoda'>): string {
  if (u.zdroj === 'rucne') return 'Na pobočce';
  if (u.zdroj === 'import') return 'Původní systém';
  return ['Přes bránu', u.metoda ? metodaNazev(u.metoda) : u.provider ? providerNazev(u.provider) : ''].filter(Boolean).join(' · ');
}

const STAV_PLATBY: Record<Platba['stav'], { label: string; tone: BadgeTone }> = {
  zalozena: { label: 'Založená', tone: 'neutral' },
  ceka: { label: 'Čeká na platbu', tone: 'warn' },
  castecne: { label: 'Částečně zaplaceno', tone: 'warn' },
  zaplaceno: { label: 'Zaplaceno', tone: 'ok' },
  zamitnuto: { label: 'Zamítnuto', tone: 'danger' },
  zruseno: { label: 'Zrušeno', tone: 'neutral' },
  expirovano: { label: 'Vypršelo', tone: 'neutral' },
  castecne_vraceno: { label: 'Částečně vráceno', tone: 'accent' },
  vraceno: { label: 'Vráceno', tone: 'neutral' },
};

export const stavPlatby = (stav: Platba['stav']) => STAV_PLATBY[stav];

const STAV_VRACENI = { ceka: 'Čeká na potvrzení brány', vraceno: 'Vráceno', selhalo: 'Brána vrácení odmítla' } as const;
export const stavVraceni = (stav: keyof typeof STAV_VRACENI): string => STAV_VRACENI[stav];

export const ZDROJ_UHRADY: Record<Uhrada['zdroj'], string> = { platba: 'Platební brána', rucne: 'Přijato na pobočce', import: 'Původní systém' };

/** The kinds of the exception queue: a short name and what happened, in the words a clerk acts on. */
export const DRUH_VYJIMKY: Record<PlatbaVyjimka['druh'], { label: string; popis: string; tone: BadgeTone }> = {
  prebytek: { label: 'Přeplatek', popis: 'Brána přijala víc, než zbývalo doplatit. Přebytek čeká na vrácení.', tone: 'warn' },
  jiz_zaplaceno: { label: 'Zaplaceno dvakrát', popis: 'Rezervace už byla zaplacená, druhá platba se nepřipsala.', tone: 'warn' },
  po_storno: { label: 'Platba po zrušení', popis: 'Platba dorazila k zrušené rezervaci, nepřipsala se.', tone: 'danger' },
  pozdni: { label: 'Pozdní platba', popis: 'Platba dorazila po vypršení; je připsaná. Jen ke vědomí.', tone: 'neutral' },
};

/**
 * The offset of the next page of a paged list, or undefined at the end: a FULL page may be followed by more, a short (or empty) one is the
 * last. `lastLen` is the length of the newest page, `lens` the lengths of all pages loaded so far.
 */
export function dalsiOffset(lastLen: number, lens: readonly number[], pageSize: number): number | undefined {
  return lastLen === pageSize ? lens.reduce((n, l) => n + l, 0) : undefined;
}

/** The typed amount as whole Kč (spaces and a trailing "Kč" allowed), or null when it is not a positive whole number in range. */
export function parseCastka(text: string, max: number = CASTKA_MAX): number | null {
  const t = text.replace(/\s| /g, '').replace(/kč$/i, '');
  if (!/^\d{1,9}$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= max ? n : null;
}

/** Why a typed amount cannot be sent (Czech, shown as it is), or null when it can. `max` = what the server would accept. */
export function castkaChyba(text: string, max: number): string | null {
  if (text.trim() === '') return 'Zadejte částku v celých korunách.';
  const n = parseCastka(text, CASTKA_MAX);
  if (n == null) return 'Částka musí být kladné celé číslo v korunách.';
  if (n > max) return `Nejvýše ${new Intl.NumberFormat('cs-CZ').format(max)} Kč.`;
  return null;
}

/** The paid column of the reservation list: nothing to pay / unpaid / part / paid, from what the list row carries. */
export type ZaplacenoStav =
  | { druh: 'bez' }
  | { druh: 'nezaplaceno'; zaplaceno: number; k_uhrade: number }
  | { druh: 'castecne'; zaplaceno: number; k_uhrade: number }
  | { druh: 'zaplaceno'; zaplaceno: number; k_uhrade: number };

export function zaplacenoStav(r: Pick<Rezervace, 'typ' | 'stav' | 'k_uhrade' | 'zaplaceno'>): ZaplacenoStav {
  // a technical block, an unpriced rental and a lease of nothing have nothing to pay; a cancelled one shows only money still held
  if (r.typ === 'technicky' || r.typ === 'servis' || r.k_uhrade == null || r.k_uhrade <= 0) return r.zaplaceno > 0 ? { druh: 'zaplaceno', zaplaceno: r.zaplaceno, k_uhrade: r.k_uhrade ?? 0 } : { druh: 'bez' };
  if (r.zaplaceno >= r.k_uhrade) return { druh: 'zaplaceno', zaplaceno: r.zaplaceno, k_uhrade: r.k_uhrade };
  if (r.zaplaceno > 0) return { druh: 'castecne', zaplaceno: r.zaplaceno, k_uhrade: r.k_uhrade };
  return r.stav === 'zrusena' ? { druh: 'bez' } : { druh: 'nezaplaceno', zaplaceno: 0, k_uhrade: r.k_uhrade };
}

/** Whether the payments block offers "Označit jako zaplaceno": a live, priced rental with something left to pay, for a writer. */
export function lzeZadatUhradu(canWrite: boolean, r: Pick<Rezervace, 'stav'>, prehled: { cena: number | null; zbyva: number }): boolean {
  return canWrite && r.stav === 'aktivni' && prehled.cena != null && prehled.zbyva > 0;
}

/** Why the payments block has nothing to take, when it has not (the line under the balance). */
export function duvodBezUhrady(r: Pick<Rezervace, 'stav'>, prehled: { cena: number | null; zbyva: number }): string | null {
  if (r.stav === 'zrusena') return 'Rezervace je zrušená — platby se už nepřijímají.';
  if (prehled.cena == null) return 'Rezervace nemá sjednanou cenu, není co platit.';
  if (prehled.zbyva <= 0) return 'Rezervace je zaplacená.';
  return null;
}

/** The Monday of the week a `YYYY-MM-DD` day falls in. */
export function pondeliTydne(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return new Date(Date.UTC(y, m - 1, d - ((t.getUTCDay() + 6) % 7))).toISOString().slice(0, 10);
}

/** A week (given by its Monday) in words: `27. 10. – 2. 11. 2026`. */
export function tydenText(pondeli: string): string {
  const [y1, m1, d1] = pondeli.split('-').map(Number);
  const [y2, m2, d2] = new Date(Date.UTC(y1, m1 - 1, d1 + 6)).toISOString().slice(0, 10).split('-').map(Number);
  return `${d1}. ${m1}.${y1 === y2 ? '' : ` ${y1}`} – ${d2}. ${m2}. ${y2}`;
}
